import { useCallback, useEffect, useRef } from 'react'

import { attemptTokenRefresh } from 'shared/api/auth/attemptTokenRefresh'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { LocalStorageKey } from 'shared/constants/localStorage'
import {
  ONE_DAY_IN_MS,
  decodeJWT,
  getTimeUntilExpiration,
  getTokenRefreshCheckInterval
} from 'shared/utils/jwt'
import { lsGetItem } from 'shared/utils/local-storage'

import { useUserContext } from './useUserContext'

const getTokenExpiryInfo = (
  accessToken: string,
  refreshToken: string
): { shouldRefresh: boolean; nextCheckBase: number } | null => {
  const decoded = decodeJWT(accessToken)
  if (!decoded?.exp) return null

  const accessRemaining = getTimeUntilExpiration(decoded.exp)
  const decodedRefresh = decodeJWT(refreshToken)
  const refreshRemaining = decodedRefresh?.exp
    ? getTimeUntilExpiration(decodedRefresh.exp)
    : null

  return {
    shouldRefresh:
      accessRemaining < ONE_DAY_IN_MS ||
      (refreshRemaining !== null && refreshRemaining < ONE_DAY_IN_MS),
    nextCheckBase:
      refreshRemaining !== null
        ? Math.min(accessRemaining, refreshRemaining)
        : accessRemaining
  }
}

export const useRefreshToken = () => {
  const previousTokenRef = useRef<string | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { user } = useUserContext()

  const scheduleNextCheck = useCallback(
    (timeUntilExpiration: number) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      const checkInterval = getTokenRefreshCheckInterval(timeUntilExpiration)

      timeoutRef.current = setTimeout(() => {
        const { access_token: accessToken, refresh_token: refreshToken } =
          user ?? {}

        if (!accessToken || !refreshToken) {
          return
        }

        const expiryInfo = getTokenExpiryInfo(accessToken, refreshToken)
        if (!expiryInfo) return

        if (expiryInfo.shouldRefresh) {
          attemptTokenRefresh()
        }

        scheduleNextCheck(expiryInfo.nextCheckBase)
      }, checkInterval)
    },
    [user]
  )

  const checkTokenUpdate = useCallback(() => {
    const { access_token: currentToken, refresh_token: refreshToken } =
      user ?? {}

    if (!currentToken || currentToken === previousTokenRef.current) {
      return
    }

    previousTokenRef.current = currentToken

    if (!refreshToken) return

    const expiryInfo = getTokenExpiryInfo(currentToken, refreshToken)
    if (!expiryInfo) return

    scheduleNextCheck(expiryInfo.nextCheckBase)
  }, [user, scheduleNextCheck])

  useEffect(() => {
    const rememberMe = lsGetItem(LocalStorageKey.REMEMBER_ME)
    if (!rememberMe && FEATURE_FLAGS.SHOW_REMEMBER_ME) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      return
    }

    checkTokenUpdate()

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [user?.access_token, checkTokenUpdate])
}
